import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Form, Input, InputNumber, Select, Modal, Upload } from 'antd';
import { Button, Message } from '@alifd/next';
import { uploadFiles, deleteFile } from '@/lib/manipulateFile';
import client from '@/lib/feathers';

const { Item } = Form;

type Subject = {
  _id: string;
  subjectCode: string;
  subjectName: string;
  used: boolean;
  activated: boolean;
  createdTime: number;
  updateTime: number;
};

type LongTermRepairIncomeSetting = {
  repairSubjectId?: string;
  repairPer?: number | null;
  repairAccountBank?: string | null;
  repairAccountNumber?: string | null;
  repairDepends?: string | null;
  repairDependsFile?: FileList | null;
};

const EditLongTermRepairIncome: React.FC<{
  setBackStageUpdateTrigger: Dispatch<SetStateAction<Record<string, never>>>;
}> = ({ setBackStageUpdateTrigger }) => {
  const [visible, setVisible] = useState(false);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [currentSetting, setCurrentSetting] =
    useState<LongTermRepairIncomeSetting | null>(null);

  const [form] = Form.useForm();

  useEffect(() => {
    const getSubjects = async () => {
      try {
        const res = await client.service('accountingSubjects').find({});
        setSubjects(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    getSubjects();
  }, []);

  useEffect(() => {
    const getCurrentSetting = async () => {
      try {
        const res = await client.service('communityData').find({
          query: {
            $select: [
              'repairSubjectId',
              'repairPer',
              'repairAccountBank',
              'repairAccountNumber',
              'repairDepends',
              'repairDependsFile',
            ],
          },
        });
        setCurrentSetting(res.data[0]);
      } catch (err) {
        console.error(err);
      }
    };
    if (visible) {
      getCurrentSetting();
    }
  }, [visible]);

  useEffect(() => {
    if (currentSetting) {
      form.setFieldsValue(currentSetting);
    }
  }, [currentSetting, form]);

  const handleEditLongTermRepairIncome = async (
    values: LongTermRepairIncomeSetting
  ) => {
    const updateValues: LongTermRepairIncomeSetting = values;
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        updateValues[key] = value;
      } else {
        updateValues[key] = null;
      }
    });
    if (Object.keys(updateValues).length > 0) {
      try {
        await client.service('communityData').patch(null, updateValues, {});
        Message.success('編輯成功');
        setVisible(false);
        setBackStageUpdateTrigger({});
      } catch (error) {
        console.error(error);
        Message.error('編輯失敗');
      }
    } else {
      Message.warning('没有任何修改');
    }
  };

  return (
    <>
      <Button onClick={() => setVisible(true)}>編輯長期修繕收入</Button>
      <Modal
        visible={visible}
        onOk={form.submit}
        onCancel={() => {
          setVisible(false);
        }}
        okText="儲存"
        cancelText="取消"
        title="編輯長期修繕收入"
      >
        <Form form={form} onFinish={handleEditLongTermRepairIncome}>
          <Item
            label="提撥科目"
            name="repairSubjectId"
            rules={[
              {
                required: true,
                message: '請選擇提撥科目',
              },
            ]}
          >
            <Select placeholder="請選擇提撥科目">
              {subjects.map(({ _id, subjectCode, subjectName }) => (
                <Select.Option key={_id} value={_id}>
                  {subjectCode} - {subjectName}
                </Select.Option>
              ))}
            </Select>
          </Item>
          <Item
            label="提撥比例"
            name="repairPer"
            rules={[
              {
                required: true,
                message: '請輸入提撥比例',
              },
            ]}
          >
            <InputNumber
              min={0}
              max={100}
              formatter={(val) => (val ? `${val}%` : '')}
            />
          </Item>
          <Item
            label="專款存款銀行"
            name="repairAccountBank"
            rules={[
              {
                required: true,
                message: '請輸入專款存款銀行',
              },
            ]}
          >
            <Input placeholder="請輸入銀行名稱" />
          </Item>
          <Item
            label="專款存款帳號"
            name="repairAccountNumber"
            rules={[
              {
                required: true,
                message: '請輸入專款存款帳號',
              },
            ]}
          >
            <Input placeholder="請輸入帳號" />
          </Item>
          <Item
            label="提撥依據"
            name="repairDepends"
            rules={[
              {
                required: true,
                message: '請輸入提撥依據',
              },
            ]}
          >
            <Input placeholder="ex:XXXX年區權會" />
          </Item>
          <Item
            label="上傳相關檔案"
            name="repairDependsFile"
            valuePropName="fileList"
            getValueFromEvent={({ file, fileList }) => {
              return fileList;
            }}
            rules={[
              {
                validator: (_, fileList) => {
                  if (fileList.length > 0) {
                    const file = fileList[0];
                    if (file.size > 20 * 1024 * 1024) {
                      return Promise.reject(
                        new Error('檔案大小不能超過20MB，請移除或重新上傳')
                      );
                    } else if (file.status === 'uploading') {
                      return Promise.reject(
                        new Error('檔案上傳中，請稍後再試')
                      );
                    } else {
                      return Promise.resolve();
                    }
                  } else {
                    return Promise.resolve();
                  }
                },
              },
            ]}
          >
            <Upload
              fileList={form.getFieldValue('repairDependsFile') || []}
              accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              beforeUpload={(file) => file.size < 20 * 1024 * 1024}
              maxCount={1}
              customRequest={async (options) => {
                const { file, onSuccess, onError } = options;
                if (onSuccess && onError) {
                  try {
                    const res = await uploadFiles('communityData', file);
                    if (onSuccess !== undefined) {
                      onSuccess(res.data);
                    }
                  } catch (error) {
                    onError(error, null);
                  }
                }
              }}
              onRemove={async (file) => {
                try {
                  await deleteFile(file);
                } catch (err) {
                  console.error({ err });
                }
              }}
            >
              <Button>上傳</Button>
            </Upload>
          </Item>
        </Form>
      </Modal>
    </>
  );
};

export default EditLongTermRepairIncome;
